import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", plan: null });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", plan: data.plan });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed", plan: null });
          return;
        }
      } catch (e) {
        setError(errMsg(e));
        return;
      }
      if (attempts < 12) setTimeout(tick, 2000);
      else setState({ status: "timeout", plan: null });
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rounded-xl border border-ink-600 bg-ink-800 p-8 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-gold" />
            <p className="mt-5 text-sm text-slate-400">Confirming your payment…</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
            <h1 className="mt-5 text-2xl font-bold text-slate-50">Upgrade complete</h1>
            <p className="mt-2 text-sm text-slate-400">Your {state.plan} features are now unlocked.</p>
          </>
        )}
        {["failed", "timeout"].includes(state.status) && (
          <>
            <XCircle className="mx-auto h-8 w-8 text-red-400" />
            <h1 className="mt-5 text-2xl font-bold text-slate-50">Payment not confirmed</h1>
            <p className="mt-2 text-sm text-slate-400">We couldn't confirm the payment yet. Check your plans page in a moment.</p>
          </>
        )}
        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
        <Button asChild className="mt-7 w-full bg-gold text-ink-900 hover:bg-gold-light">
          <Link to="/app/plans" data-testid="payment-success-continue">Back to plans</Link>
        </Button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rounded-xl border border-ink-600 bg-ink-800 p-8 text-center">
        <XCircle className="mx-auto h-8 w-8 text-slate-500" />
        <h1 className="mt-5 text-2xl font-bold text-slate-50">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-slate-400">No charge was made. Your current plan is unchanged.</p>
        <Button asChild className="mt-7 w-full bg-gold text-ink-900 hover:bg-gold-light">
          <Link to="/app/plans" data-testid="payment-cancel-back">Back to plans</Link>
        </Button>
      </div>
    </div>
  );
}
